import { AxiosRequestConfig } from 'axios';
import { Moment } from 'moment';
import qs from 'qs';
import { HttpResponse, ajax } from '../../../../common/ajax';
import { config } from '../../config';
import {
  AddAttendenceTypeData,
  AttendenceTypeModel,
  UpdateAttendenceTypeData,
} from '../../models/AttendenceType';
import {
  CompanyAttendanceSettingsData,
  CompanySettingsModel,
} from '../../models/CompanyAttendenceSettings';
import { EmployeeTimesheetModel } from '../../models/EmployeeTimesheetModel';
import { EmployeeTimesheetSummaryModel } from '../../models/EmployeeTimesheetSummaryModel';

interface ITimeAndAttendanceAdminService {
  getTimesheets: (
    queryString?: string,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<EmployeeTimesheetModel[]>>;
  getAttendenceTypes: (
    queryString?: string,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<AttendenceTypeModel[]>>;
  getCompanySettings: (
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<CompanySettingsModel>>;
  addAttendenceType: (
    data: AddAttendenceTypeData,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<boolean>>;
  updateAttendenceType: (
    data: UpdateAttendenceTypeData,
    axionConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<boolean>>;
  approveTimesheets: (
    employeesIds: number[],
    month: Moment,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<boolean>>;
  reopenTimesheets: (
    employeesIds: number[],
    month: Moment,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<boolean>>;
  getTimesheetSummary: (
    employeesId: number,
    month: Moment,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<EmployeeTimesheetSummaryModel[]>>;
  saveCompanyAttendenceSettings: (
    data: CompanyAttendanceSettingsData,
    axionConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<boolean>>;
  sendTestEmail: (
    emailBody: string,
    axionConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<boolean>>;
  sendRemiderEmail: (
    employeeId: number,
    axionConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<boolean>>;
  deleteAttendenceType: (
    attendenceTypeId: number,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<boolean>>;
  downloadAttendanceReport: (
    year: number,
    month: number,
    selectedEmployeeIds: number[],
    filename: string,
    axiosConfig?: AxiosRequestConfig,
  ) => Promise<HttpResponse<any>>;
}

export const timesheetsAdminService: ITimeAndAttendanceAdminService = {
  getTimesheets(
    queryString,
    axiosConfig,
  ): Promise<HttpResponse<EmployeeTimesheetModel[]>> {
    return ajax.get<EmployeeTimesheetModel[]>(
      `${config.benefitApiUrl}/odata/timesheets?${queryString}`,
      axiosConfig,
    );
  },
  getAttendenceTypes(
    queryString,
    axiosConfig,
  ): Promise<HttpResponse<AttendenceTypeModel[]>> {
    return ajax.get<AttendenceTypeModel[]>(
      `${config.benefitApiUrl}/odata/attendence-types?${queryString}`,
      axiosConfig,
    );
  },
  getCompanySettings(axiosConfig): Promise<HttpResponse<CompanySettingsModel>> {
    return ajax.get<CompanySettingsModel>(
      `${config.benefitApiUrl}/company-settings`,
      axiosConfig,
    );
  },
  addAttendenceType(
    data: AddAttendenceTypeData,
    axiosConfig?: AxiosRequestConfig,
  ): Promise<HttpResponse<boolean>> {
    return ajax.post<boolean>(
      `${config.benefitApiUrl}/add-attendence-type`,
      data,
      axiosConfig,
    );
  },
  updateAttendenceType(
    data: UpdateAttendenceTypeData,
    axiosConfig?: AxiosRequestConfig,
  ): Promise<HttpResponse<boolean>> {
    return ajax.put<boolean>(
      `${config.benefitApiUrl}/update-attendence-type`,
      data,
      axiosConfig,
    );
  },
  approveTimesheets(employeesIds, month, axiosConfig) {
    return ajax.put<boolean>(
      `${config.benefitApiUrl}/timesheets/approve`,
      {
        employeesIds: employeesIds,
        month: month.format('YYYY-MM-DD'),
      },
      axiosConfig,
    );
  },
  reopenTimesheets(employeesIds, month, axiosConfig) {
    return ajax.put<boolean>(
      `${config.benefitApiUrl}/timesheets/reset-submit`,
      {
        employeesIds: employeesIds,
        month: month.format('YYYY-MM-DD'),
      },
      axiosConfig,
    );
  },
  getTimesheetSummary(
    employeesId: number,
    month: Moment,
    axiosConfig?: AxiosRequestConfig,
  ): Promise<HttpResponse<EmployeeTimesheetSummaryModel[]>> {
    return ajax.get<EmployeeTimesheetSummaryModel[]>(
      `${
        config.benefitApiUrl
      }/timesheet-summary?customerId=${employeesId}&month=${month.format(
        'YYYY-MM-DD',
      )}`,
      axiosConfig,
    );
  },
  saveCompanyAttendenceSettings(
    data: CompanyAttendanceSettingsData,
    axiosConfig?: AxiosRequestConfig,
  ): Promise<HttpResponse<boolean>> {
    return ajax.put<boolean>(
      `${config.benefitApiUrl}/company-timesheet-settings`,
      data,
      axiosConfig,
    );
  },
  sendTestEmail(
    emailBody: string,
    axiosConfig?: AxiosRequestConfig,
  ): Promise<HttpResponse<boolean>> {
    return ajax.put<boolean>(
      `${config.benefitApiUrl}/send-test-email`,
      { emailBody },
      axiosConfig,
    );
  },
  sendRemiderEmail(employeeId, axionConfig) {
    return ajax.put<boolean>(
      `${config.benefitApiUrl}/send-reminder-email`,
      { employeeId: employeeId },
      axionConfig,
    );
  },
  deleteAttendenceType(
    attendenceTypeId: number,
    axiosConfig?: AxiosRequestConfig,
  ): Promise<HttpResponse<boolean>> {
    return ajax.delete<boolean>(
      `${config.benefitApiUrl}/delete-attendence-type/${attendenceTypeId}`,
      axiosConfig,
    );
  },

  downloadAttendanceReport(
    year: number,
    month: number,
    ids: number[],
    fileName,
    axiosConfig?: AxiosRequestConfig,
  ): Promise<HttpResponse<any>> {
    const params = { year, month, ids };
    return ajax.downloadFile(
      `${config.benefitApiUrl}/download-attendance-report`,
      fileName,
      'xlsx',
      {
        ...axiosConfig,
        params,
        paramsSerializer: params => qs.stringify(params),
      },
    );
  },
};
